import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getAuthHeaders, makeApiCall } from '../../utils/apiConfig';
import { useUnsavedChangesSource } from '../../components/appUpdate';
import { useOrgFeatureFlag, useOrgFeatureFlags } from '../../hooks/useOrgFeatureFlags';
import {
  addWidget,
  addableWidgets,
  buildDefaultLayout,
  configureWidget,
  insertWidgetAt,
  layoutsDiffer,
  moveWidget,
  moveWidgetTo,
  widgetDefinition,
  removeWidget,
  resizeWidget,
  resolveDashboardLayout,
  toLayout,
  usesFixedLayout,
  type AddableWidget,
  type DashboardLayout,
  type DashboardShell,
  type LayoutWidget,
  type ServerDashboardLayout,
  type WidgetConfig,
  type WidgetSize,
} from './widgetLayout';

const layoutQueryKey = (shell: DashboardShell, orgKey: string) => ['dashboard-layout', shell, orgKey] as const;

async function readError(response: Response, fallback: string): Promise<Error> {
  const body = await response.json().catch(() => null);
  return new Error(body?.message || fallback);
}

/**
 * Everything a home page needs to be customizable (flag `customizable_dashboards`):
 * the saved layout resolved against flags and plan, the edit-mode draft, and the
 * save / reset / org-default calls. With the flag off `enabled` is false and the
 * page renders its fixed layout untouched.
 */
export interface DashboardToast {
  message: string;
  /** Present when the action can be reverted from the toast. */
  undo?: () => void;
}

export const useDashboardLayout = (
  shell: DashboardShell,
  organizationId: string | undefined,
  organizationName?: string,
  planLockedIds?: readonly string[],
) => {
  const { t } = useTranslation();
  const tt = t as unknown as (key: string, options?: Record<string, unknown>) => string;
  const queryClient = useQueryClient();
  const { enabled } = useOrgFeatureFlag('customizable_dashboards');
  const { isEnabled, sourceOf } = useOrgFeatureFlags();
  const showLockedFeatures = isEnabled('show_locked_plan_features');
  const orgKey = organizationId || 'self';
  // The draft belongs to the organization it was started for: after a switch it
  // reads as no draft, so it can never be saved onto the new acting organization.
  const [draftState, setDraftState] = useState<{ orgKey: string; widgets: LayoutWidget[] } | null>(null);
  const draft = draftState && draftState.orgKey === orgKey ? draftState.widgets : null;
  const setDraft = useCallback(
    (next: LayoutWidget[] | null) => setDraftState(next ? { orgKey, widgets: next } : null),
    [orgKey]
  );

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [toast, setToast] = useState<DashboardToast | null>(null);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const dismissToast = useCallback(() => {
    clearTimeout(toastTimer.current);
    setToast(null);
  }, []);
  const flash = useCallback((next: DashboardToast) => {
    clearTimeout(toastTimer.current);
    setToast(next);
    toastTimer.current = setTimeout(() => setToast(null), 5000);
  }, []);
  useEffect(() => () => clearTimeout(toastTimer.current), []);

  const query = useQuery({
    queryKey: layoutQueryKey(shell, orgKey),
    enabled,
    staleTime: 30_000,
    queryFn: async (): Promise<ServerDashboardLayout | null> => {
      const headers = await getAuthHeaders();
      const response = await makeApiCall(`/session/dashboard-layout?shell=${shell}`, { headers });
      // A failed read is an error, not "nothing saved": only a 2xx with no layout means that.
      if (!response.ok) throw await readError(response, 'Could not load the dashboard layout');
      const body = await response.json();
      return body?.data ?? null;
    },
  });
  const server = enabled ? query.data ?? null : null;
  // `data` is undefined until a read succeeds and null once one says nothing is saved.
  const loaded = enabled && query.data !== undefined;
  const loadError = enabled && query.data === undefined ? (query.error as Error | null) : null;

  const ctx = useMemo(
    () => ({
      server,
      isFlagEnabled: isEnabled,
      includePlanLocked: showLockedFeatures,
      planLockedIds,
      flagSource: sourceOf,
    }),
    [server, isEnabled, showLockedFeatures, sourceOf, planLockedIds],
  );
  const saved = useMemo(() => resolveDashboardLayout(shell, ctx), [shell, ctx]);
  const editing = draft !== null;
  const widgets = draft ?? saved;
  const fixed = usesFixedLayout(server) && !editing;

  const put = useCallback(
    async (url: string, layout: DashboardLayout | null) => {
      const headers = { ...(await getAuthHeaders()), 'Content-Type': 'application/json' };
      const response = await makeApiCall(url, { method: 'PUT', headers, body: JSON.stringify({ shell, layout }) });
      if (!response.ok) throw await readError(response, 'Could not save the dashboard layout');
      return response;
    },
    [shell]
  );

  const saveMutation = useMutation({
    mutationFn: async ({ layout, forOrg }: { layout: DashboardLayout | null; forOrg: string }) => {
      const response = await put('/session/dashboard-layout', layout);
      return { forOrg, data: (await response.json())?.data as ServerDashboardLayout };
    },
    onSuccess: ({ forOrg, data }) => {
      queryClient.setQueryData(layoutQueryKey(shell, forOrg), data);
      setDraftState((current) => (current && current.orgKey === forOrg ? null : current));
      setDrawerOpen(false);
      flash({ message: tt('dashboardLayout.toast.saved') });
    },
  });

  const orgDefaultMutation = useMutation({
    mutationFn: async ({ layout, forOrg }: { layout: DashboardLayout | null; forOrg: string | undefined }) => {
      if (!forOrg) throw new Error('No organization selected');
      await put(`/organizations/${forOrg}/dashboard-layout`, layout);
      return forOrg;
    },
    onSuccess: (forOrg) => {
      queryClient.invalidateQueries({ queryKey: layoutQueryKey(shell, forOrg) });
      flash({
        message: organizationName
          ? tt('dashboardLayout.toast.orgSaved', { org: organizationName })
          : tt('dashboardLayout.toast.orgSavedGeneric'),
      });
    },
  });

  // Save results and errors from the previous organization do not carry over.
  const { reset: resetSave } = saveMutation;
  const { reset: resetOrgDefault } = orgDefaultMutation;
  useEffect(() => {
    resetSave();
    resetOrgDefault();
  }, [orgKey, resetSave, resetOrgDefault]);

  const addable: AddableWidget[] = useMemo(
    () => addableWidgets(shell, widgets, ctx),
    [shell, widgets, ctx]
  );

  const update = (fn: (w: LayoutWidget[]) => LayoutWidget[]) => setDraft(fn(draft ?? saved));
  const isDirty = draft !== null && layoutsDiffer(draft, saved);

  // Leaving the page with unsaved edits asks first.
  useEffect(() => {
    if (!isDirty) return undefined;
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [isDirty]);

  const titleOf = (id: string) => {
    const def = widgetDefinition(shell, id);
    return def ? tt(`dashboardLayout.widgets.${def.labelKey}`) : id;
  };
  // Revert to a snapshot of the draft taken before the change the toast reports.
  const withUndo = (message: string, previous: LayoutWidget[]) =>
    flash({ message, undo: () => { setDraft(previous); dismissToast(); } });

  const cancel = () => {
    setDraft(null);
    setDrawerOpen(false);
    dismissToast();
    saveMutation.reset();
    orgDefaultMutation.reset();
  };

  // Tell the app-update prompt about the draft so it offers Save / Discard instead of reloading it away.
  useUnsavedChangesSource({
    dirty: isDirty,
    save: () => saveMutation.mutateAsync({ layout: toLayout(widgets), forOrg: orgKey }),
    discard: cancel,
  });

  return {
    enabled,
    /** The saved layout has been read OK (possibly "nothing saved"); Customize waits for this. */
    loaded,
    loadError,
    /** Render the page's original markup (nothing saved, not editing). */
    fixed,
    editing,
    widgets,
    addable,
    // Never seed a draft from the built-in default while the real layout is unknown.
    startEditing: () => {
      if (!loaded) return;
      setDraft(saved.length > 0 ? saved : buildDefaultLayout(shell).widgets);
    },
    isDirty,
    drawerOpen: editing && drawerOpen,
    toggleDrawer: () => setDrawerOpen((open) => !open),
    closeDrawer: () => setDrawerOpen(false),
    toast,
    dismissToast,
    cancel,
    /** `visibleIds` is what the grid actually renders; a widget with nothing to show is skipped over. */
    move: (id: string, delta: -1 | 1, visibleIds?: string[]) => update((w) => moveWidget(w, id, delta, visibleIds)),
    resize: (id: string, size: WidgetSize) => update((w) => resizeWidget(shell, w, id, size)),
    /** Drop a widget into the slot a drag reported. */
    moveTo: (id: string, toIndex: number, visibleIds?: string[]) => update((w) => moveWidgetTo(w, id, toIndex, visibleIds)),
    /** Hide a widget; the toast offers Undo. */
    remove: (id: string) => {
      const previous = widgets;
      update((w) => removeWidget(w, id));
      withUndo(tt('dashboardLayout.toast.hidden', { title: titleOf(id) }), previous);
    },
    add: (id: string) => {
      update((w) => addWidget(shell, w, id));
      flash({ message: tt('dashboardLayout.toast.added', { title: titleOf(id) }) });
    },
    /** Place a widget from the Widgets panel at a position in the grid. */
    insertAt: (id: string, index: number) => {
      update((w) => insertWidgetAt(shell, w, id, index));
      flash({ message: tt('dashboardLayout.toast.added', { title: titleOf(id) }) });
    },
    configure: (id: string, config: WidgetConfig) => update((w) => configureWidget(w, id, config)),
    /** Plan availability the server resolved per widget; the Key numbers tile derives its locked numbers from it. */
    availability: server?.availability,
    /**
     * Set the Key numbers tile's list. `removedLabel` names a number the user just removed:
     * the toast then offers Undo, which puts the previous list back.
     */
    setKpis: (kpis: string[], removedLabel?: string) => {
      const previous = widgets;
      update((w) => w.map((x) => (x.id === 'kpis' ? { ...x, config: { ...x.config, kpis } } : x)));
      if (removedLabel) withUndo(tt('dashboardLayout.kpis.removed', { label: removedLabel }), previous);
    },
    save: () => saveMutation.mutate({ layout: toLayout(widgets), forOrg: orgKey }),
    /** Put the standard layout in the draft; nothing is saved until Save layout. */
    reset: () => {
      const previous = widgets;
      // The standard layout minus what this organization cannot have (flag off, plan-locked): the server rejects those.
      setDraft(resolveDashboardLayout(shell, { server: server ? { ...server, layout: null } : null, isFlagEnabled: isEnabled }));
      withUndo(tt('dashboardLayout.toast.reset'), previous);
    },
    saveAsOrganizationDefault: () => orgDefaultMutation.mutate({ layout: toLayout(widgets), forOrg: organizationId }),
    saving: saveMutation.isPending || orgDefaultMutation.isPending,
    error: (saveMutation.error || orgDefaultMutation.error) as Error | null,
    orgDefaultSaved: orgDefaultMutation.isSuccess,
    shell,
  };
};

export type DashboardLayoutController = ReturnType<typeof useDashboardLayout>;
